import { app, authentication } from "@microsoft/teams-js";
import { TeamsApplicationTypes, TeamsSurveyQnaResponse, TeamsSurveyResponse } from "@teams/types";
import {
  ChannelAccountDTO,
  ChannelInfoDTO,
  QuestionnaireTemplateDTO,
  ScheduleDTO,
  SurveyDTO,
  TeamInfoDTO,
} from "@teams/types/api";

import { getPageContext } from "../utils/teamsHelpers";

export type FetchResult<T> = T | Error;

export const encodeApiUrl = (strings: TemplateStringsArray, ...keys: Array<string>) => {
  const pageContext = getPageContext();
  const out = strings.reduce((arr: Array<string>, string, i) => {
    return arr.concat(string, encodeURIComponent(keys[i]));
  }, []);
  out.pop();
  return `${pageContext}/api${out.join("")}`;
};

export const isError = <T>(result: FetchResult<T>): result is Error => result instanceof Error;
export const isSuccess = <T>(result: FetchResult<T>): result is T => !isError(result);

export type ChannelSurveysOptions = {
  includeResponses?: boolean;
};

export const channelApi = {
  teamMembers: (context: app.Context): Promise<FetchResult<Array<ChannelAccountDTO>>> => {
    const groupId = getGroupIdFromContext(context);
    if (groupId === null) {
      console.error("Unable to load team members", context);
      return Promise.resolve(new Error("Unable to load team members"));
    }

    return fetchApiWithContext(context, encodeApiUrl`/v1/teams/${groupId}/members`);
  },
  teamChannels: (context: app.Context): Promise<FetchResult<Array<ChannelInfoDTO>>> => {
    const groupId = getGroupIdFromContext(context);
    if (groupId === null) {
      console.error("Unable to load team channels", context);
      return Promise.resolve(new Error("Unable to load team channels"));
    }

    return fetchApiWithContext(context, encodeApiUrl`/v1/teams/${groupId}/channels`);
  },
  installedApplications: (context: app.Context): Promise<FetchResult<TeamsApplicationTypes[]>> => {
    const groupId = getGroupIdFromContext(context);
    if (groupId === null) {
      console.error("Unable to load installed applications for team", context);
      return Promise.resolve(new Error("Unable to load installed applications for team"));
    }

    return fetchApiWithContext(context, encodeApiUrl`/v1/teams/${groupId}/applications`);
  },
  tabAdded: (
    context: app.Context,
    teamId: string,
    entityId: string,
    channelId?: string,
    meetingId?: string
  ): Promise<FetchResult<void>> => {
    return fetchApiWithContext(context, encodeApiUrl`/v1/teams/${teamId}/tab/add`, {
      method: "POST",
      body: JSON.stringify({
        meetingId,
        entityId,
        channelId,
      }),
    });
  },
  tabRemoved: (
    context: app.Context,
    teamId: string,
    entityId: string
  ): Promise<FetchResult<void>> => {
    return fetchApiWithContext(context, encodeApiUrl`/v1/teams/${teamId}/tab/${entityId}`, {
      method: "DELETE",
    });
  },
  getTeamInfo: (
    context: app.Context,
    teamId?: string,
    fetchCountFromRemote?: boolean
  ): Promise<FetchResult<TeamInfoDTO>> => {
    const id = teamId || context.team?.internalId || (context.chat?.id as string);

    const queryParams = new URLSearchParams();

    if (fetchCountFromRemote) {
      queryParams.append("fetchCountFromRemote", "true");
    }

    return fetchApiWithContext(context, encodeApiUrl`/v1/teams/${id}/info`, { queryParams });
  },
};

export type FetchOptions = RequestInit & {
  queryParams?: URLSearchParams;
};

export async function fetchApiWithContext<T>(
  _context: app.Context,
  uri: string,
  init?: FetchOptions,
  returnText?: boolean
): Promise<FetchResult<T>> {
  const bearerToken = await authentication.getAuthToken({});

  return await fetchWithToken<T>(uri, bearerToken, init, returnText);
}

export async function fetchNoToken<T>(uri: string, init?: FetchOptions): Promise<T> {
  let requestInit: RequestInit = init || {};
  requestInit.headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
  });

  return fetch(uri, requestInit).then(async response => {
    if (!response.ok) {
      let body;
      try {
        body = await response.json();
      } catch (error) {}
      return new FetchError(response.status, response.statusText, body);
    }
    return response.json();
  });
}

export async function fetchWithToken<T>(
  uri: string,
  token: string,
  init?: FetchOptions,
  returnText?: boolean
): Promise<T> {
  let requestInit: RequestInit = init || {};
  requestInit.headers = new Headers({
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
    "Content-Type": "application/json",
  });

  const url = new URL(uri, window.location.origin);
  url.search = init && init.queryParams ? init.queryParams.toString() : "";

  return fetch(url.toString(), requestInit).then(async response => {
    if (!response.ok) {
      let body;
      try {
        body = await response.json();
      } catch (error) {}
      const message =
        response.statusText || `Server responded with an error status: ${response.status}`;
      return new FetchError(response.status, message, body);
    }
    if (returnText) {
      return response.text();
    }
    return response.json();
  });
}

export const getGroupIdFromContext = (context: app.Context): string | null => {
  if (context.team?.internalId) {
    return context.team?.internalId;
  } else if (context.chat?.id) {
    return context.chat?.id;
  }
  return null;
};

export const deserializeSurveyDTO = (survey: SurveyDTO): void => {
  if (survey.deliverAt) {
    survey.deliverAt = new Date(survey.deliverAt);
  }
  if (survey.closeAt) {
    survey.closeAt = new Date(survey.closeAt);
  }

  survey.updatedAt = new Date(survey.updatedAt);
  survey.createdAt = new Date(survey.createdAt);

  if (survey.responseUpdatedAt) {
    survey.responseUpdatedAt = new Date(survey.responseUpdatedAt);
  }

  if (survey.responses) {
    survey.responses.forEach(deserializeResponse);
  }

  survey.questions.forEach(q => {
    if (q.openDate) {
      q.openDate = new Date(q.openDate);
    }
    if (q.closeDate) {
      q.closeDate = new Date(q.closeDate);
    }
  });

  if (survey.responses) {
    survey.responses.forEach(deserializeResponse);
  }
};

export const deserializeScheduleDTO = (schedule: ScheduleDTO): void => {
  if (schedule.lastSurvey) {
    deserializeSurveyDTO(schedule.lastSurvey);
  }
};

export const deserializeTemplateDTO = (template: QuestionnaireTemplateDTO): void => {
  template.updatedAt = new Date(template.updatedAt);
};

export const deserializeResponse = (response: TeamsSurveyResponse): void => {
  response.createdAt = new Date(response.createdAt);
  response.updatedAt = new Date(response.updatedAt);
  response.responses.forEach(q => {
    q.createdAt = new Date(q.createdAt);
    q.updatedAt = new Date(q.updatedAt || q.createdAt);
  });
  if (response.comments) {
    response.comments.forEach(c => {
      c.createdAt = new Date(c.createdAt);
    });
  }
  if (response.completedOn) {
    response.completedOn = new Date(response.completedOn);
  }
  const qnaResponse = response as TeamsSurveyQnaResponse;
  if (qnaResponse?.stateChangedAt) {
    qnaResponse.stateChangedAt = new Date(qnaResponse.stateChangedAt);
  }
};

export class FetchError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly statusText: string,
    public readonly body?: Record<string, unknown>
  ) {
    super(statusText);
    this.name = "FetchError";
  }
}

export const isFetchError = (error: unknown): error is FetchError => {
  return error instanceof FetchError;
};

export const extractFetchErrorMessage = (error: FetchError): string => {
  if (error.body) {
    if (error.body.error) return error.body.error.toString();
    if (error.body.message) return error.body.message.toString();
  }
  return error.message;
};
